import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import AppLayout from '@/components/Layout/AppLayout';
import NotFound from '@/components/Common/NotFound';
import LoadingSpinner from '@/components/Common/LoadingSpinner';
import ErrorBoundary from '@/components/Common/ErrorBoundary';
import RouteGate from '@/components/Common/RouteGate';
import { homePathFor } from '@/config/employeeExperience';

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// ---------------------------------------------------------------------------

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const SetPasswordPage = lazy(() => import('@/pages/SetPasswordPage'));

// /dashboard: DashboardPage, or a redirect to /my-day for the employee
// experience — pages/HomePage chooses, by permission (Employee B1, C1).
const HomePage = lazy(() => import('@/pages/HomePage'));
// Employee rebuild C1: My Day, the employee experience's home.
const MyDayPage = lazy(() => import('@/pages/MyDayPage'));
// FM Manager rebuild W1: the FM home (C1) and SLA & escalation settings (C28).
const FmHomePage = lazy(() => import('@/pages/fm/FmHomePage'));
const FmSettingsPage = lazy(() => import('@/pages/fm/FmSettingsPage'));
const FmTicketsPage = lazy(() => import('@/pages/fm/FmTicketsPage'));
const FmSpotChecksPage = lazy(() => import('@/pages/fm/FmSpotChecksPage'));
const FmLogbooksPage = lazy(() => import('@/pages/fm/FmLogbooksPage'));
// W5: the PPM planner (C11) and assets (C13).
const FmPpmPage = lazy(() => import('@/pages/fm/FmPpmPage'));
const FmAssetsPage = lazy(() => import('@/pages/fm/FmAssetsPage'));
const FmPermitsPage = lazy(() => import('@/pages/fm/FmPermitsPage'));
const FmVendorsPage = lazy(() => import('@/pages/fm/FmVendorsPage'));
const FmAmcPage = lazy(() => import('@/pages/fm/FmAmcPage'));
const FmCompliancePage = lazy(() => import('@/pages/fm/FmCompliancePage'));
const FmInvoicesPage = lazy(() => import('@/pages/fm/FmInvoicesPage'));
const FmDrillsPage = lazy(() => import('@/pages/fm/FmDrillsPage'));
const FmIncidentsPage = lazy(() => import('@/pages/fm/FmIncidentsPage'));
// FM Technician rebuild, R1.
const FMDesignatedPersonsPage = lazy(() => import('@/pages/fm/FMDesignatedPersonsPage'));
const FMTechniciansPage = lazy(() => import('@/pages/fm/FMTechniciansPage'));
const FMProofRulesPage = lazy(() => import('@/pages/fm/FMProofRulesPage'));
const ReceptionHomePage = lazy(() => import('@/pages/reception/ReceptionHomePage'));
const ReceptionCheckInPage = lazy(() => import('@/pages/reception/ReceptionCheckInPage'));
const ReceptionVisitorsPage = lazy(() => import('@/pages/reception/ReceptionVisitorsPage'));
const ReceptionPreRegisterPage = lazy(() => import('@/pages/reception/ReceptionPreRegisterPage'));
const ReceptionApprovalsPage = lazy(() => import('@/pages/reception/ReceptionApprovalsPage'));
const ReceptionWatchlistPage = lazy(() => import('@/pages/reception/ReceptionWatchlistPage'));
const ReceptionAlertsPage = lazy(() => import('@/pages/reception/ReceptionAlertsPage'));
const ReceptionProfilePage = lazy(() => import('@/pages/reception/ReceptionProfilePage'));
const ReceptionRegistersPage = lazy(() => import('@/pages/reception/ReceptionRegistersPage'));
const ReceptionMailroomPage = lazy(() => import('@/pages/reception/ReceptionMailroomPage'));
const ReceptionGatePassesPage = lazy(() => import('@/pages/reception/ReceptionGatePassesPage'));
const ReceptionContractorWorkersPage = lazy(() => import('@/pages/reception/ReceptionContractorWorkersPage'));
const ReceptionGatePassNewPage = lazy(() => import('@/pages/reception/ReceptionGatePassNewPage'));
const GatePassDecisionPage = lazy(() => import('@/pages/reception/GatePassDecisionPage'));
const ReceptionKeysCardsPage = lazy(() => import('@/pages/reception/ReceptionKeysCardsPage'));
const ReceptionLostFoundPage = lazy(() => import('@/pages/reception/ReceptionLostFoundPage'));
const BlankRegisterPage = lazy(() => import('@/pages/reception/BlankRegisterPage'));
const VisitDecisionPage = lazy(() => import('@/pages/reception/VisitDecisionPage'));
const HostAnswerPage = lazy(() => import('@/pages/reception/HostAnswerPage'));
const VisitorPassPage = lazy(() => import('@/pages/reception/VisitorPassPage'));
const ReceptionRoomsPage = lazy(() => import('@/pages/reception/ReceptionRoomsPage'));
const ReceptionParkingPage = lazy(() => import('@/pages/reception/ReceptionParkingPage'));
const ReceptionEmergencyPage = lazy(() => import('@/pages/reception/ReceptionEmergencyPage'));
const ReceptionRollCallPage = lazy(() => import('@/pages/reception/ReceptionRollCallPage'));
const ReceptionDrillLogPage = lazy(() => import('@/pages/reception/ReceptionDrillLogPage'));
const ReceptionConcernPage = lazy(() => import('@/pages/reception/ReceptionConcernPage'));
const ConcernInboxPage = lazy(() => import('@/pages/reception/ConcernInboxPage'));
const ReceptionDailyReportPage = lazy(() => import('@/pages/reception/ReceptionDailyReportPage'));
const ReceptionHandoverPage = lazy(() => import('@/pages/reception/ReceptionHandoverPage'));
const ReceptionVisitorLogPage = lazy(() => import('@/pages/reception/ReceptionVisitorLogPage'));
const NoSitePage = lazy(() => import('@/pages/tech/NoSitePage'));
const NoBuildingPage = lazy(() => import('@/pages/reception/NoBuildingPage'));
const PmNoBuildingPage = lazy(() => import('@/pages/pm/PmNoBuildingPage'));
const PmHomePage = lazy(() => import('@/pages/pm/PmHomePage'));
const PmCalendarPage = lazy(() => import('@/pages/pm/PmCalendarPage'));
const PmRentRollPage = lazy(() => import('@/pages/pm/PmRentRollPage'));
const PmTenancyPage = lazy(() => import('@/pages/pm/PmTenancyPage'));
const PmTenancyFormPage = lazy(() => import('@/pages/pm/PmTenancyPage').then((m) => ({ default: m.PmTenancyFormPage })));
const PmTenantsPage = lazy(() => import('@/pages/pm/PmTenantsPage'));
const PmTenant360Page = lazy(() => import('@/pages/pm/PmTenantsPage').then((m) => ({ default: m.PmTenant360Page })));
const PmBillingPage = lazy(() => import('@/pages/pm/PmBillingPage'));
const PmCollectionsPage = lazy(() => import('@/pages/pm/PmCollectionsPage'));
const PmDepositsPage = lazy(() => import('@/pages/pm/PmDepositsPage'));
const PmApprovalsPage = lazy(() => import('@/pages/pm/PmApprovalsPage'));
const PmProfilePage = lazy(() => import('@/pages/pm/PmProfilePage'));
const AdminReceptionPage = lazy(() => import('@/pages/reception/AdminReceptionPage'));
const TechIncidentPage = lazy(() => import('@/pages/tech/TechIncidentPage'));
const TechHandoverPage = lazy(() => import('@/pages/tech/TechHandoverPage'));
const TechLeadPage = lazy(() => import('@/pages/tech/TechLeadPage'));
const TechMyWorkPage = lazy(() => import('@/pages/tech/TechMyWorkPage'));
const TechPermitPage = lazy(() => import('@/pages/tech/TechPermitPage'));
// FM Technician R3, slice S9 (B34–B36; C11, C12, C18, C27).
const TechPermitsPage = lazy(() => import('@/pages/tech/TechPermitsPage'));
const TechLocksPage = lazy(() => import('@/pages/tech/TechLocksPage'));
const TechEmergencyPage = lazy(() => import('@/pages/tech/TechEmergencyPage'));
const TechToolsPage = lazy(() => import('@/pages/tech/TechToolsPage'));
const FMToolsPage = lazy(() => import('@/pages/fm/FMToolsPage'));
const FMLocksPage = lazy(() => import('@/pages/fm/FMLocksPage'));
const TechHomePage = lazy(() => import('@/pages/tech/TechHomePage'));
const TechJobsPage = lazy(() => import('@/pages/tech/TechJobsPage'));
const TechJobPage = lazy(() => import('@/pages/tech/TechJobPage'));
const SafetyIncidentPage = lazy(() => import('@/pages/tech/SafetyIncidentPage'));
const TechAlertsPage = lazy(() => import('@/pages/tech/TechAlertsPage'));
const TechProfilePage = lazy(() => import('@/pages/tech/TechProfilePage'));
const TechFirstRunPage = lazy(() => import('@/pages/tech/TechFirstRunPage'));
const TechSyncPage = lazy(() => import('@/pages/tech/TechSyncPage'));
const TechScanPage = lazy(() => import('@/pages/tech/TechScanPage'));
const TechAssetPage = lazy(() => import('@/pages/tech/TechAssetPage'));
const TechReportFaultPage = lazy(() => import('@/pages/tech/TechReportFaultPage'));
const TechRoundsPage = lazy(() => import('@/pages/tech/TechRoundsPage'));
const TechRoundPage = lazy(() => import('@/pages/tech/TechRoundPage'));
const FmReportsPage = lazy(() => import('@/pages/fm/FmReportsPage'));
const FmKpiPage = lazy(() => import('@/pages/fm/FmKpiPage'));
// Employee rebuild C4, C5.
const OfficeDaysPage = lazy(() => import('@/pages/OfficeDaysPage'));
const WhosInPage = lazy(() => import('@/pages/WhosInPage'));
const SpacePage = lazy(() => import('@/pages/SpacePage'));
const HelpdeskPage = lazy(() => import('@/pages/HelpdeskPage'));
const TicketDetailPage = lazy(() => import('@/pages/TicketDetailPage'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage'));
const WorkOrderDetailPage = lazy(() => import('@/pages/WorkOrderDetailPage'));
const AssetsPage = lazy(() => import('@/pages/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage'));
const VisitorPage = lazy(() => import('@/pages/VisitorPage'));
const VisitorDetailPage = lazy(() => import('@/pages/VisitorDetailPage'));
const KioskPage = lazy(() => import('@/pages/KioskPage'));
const LeasePage = lazy(() => import('@/pages/LeasePage'));
const LeaseDetailPage = lazy(() => import('@/pages/LeaseDetailPage'));
const ContractorPage = lazy(() => import('@/pages/ContractorPage'));
const ContractorProfilePage = lazy(() => import('@/pages/ContractorProfilePage'));
const TenantPage = lazy(() => import('@/pages/TenantPage'));
const TenantOpsPage = lazy(() => import('@/pages/TenantOpsPage'));
const IoTPage = lazy(() => import('@/pages/IoTPage'));
const MachineDetailPage = lazy(() => import('@/pages/MachineDetailPage'));
const ParkingPage = lazy(() => import('@/pages/ParkingPage'));
const MyBookingsPage = lazy(() => import('@/pages/MyBookingsPage'));
const BookPage = lazy(() => import('@/pages/BookPage'));
// Employee rebuild C14 / B11: the catering menu and the pantry's orders.
const AdminCateringPage = lazy(() => import('@/pages/AdminCateringPage'));
const MyRequestsPage = lazy(() => import('@/pages/MyRequestsPage'));
const MyEquipmentPage = lazy(() => import('@/pages/MyEquipmentPage'));
const MyVisitorsPage = lazy(() => import('@/pages/MyVisitorsPage'));
// Employee rebuild C11 / B28.
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const MyDataDownloadPage = lazy(() => import('@/pages/MyDataDownloadPage'));
const WellbeingPage = lazy(() => import('@/pages/WellbeingPage'));
const EnergyPage = lazy(() => import('@/pages/EnergyPage'));
const CompliancePage = lazy(() => import('@/pages/CompliancePage'));
const SafetyPage = lazy(() => import('@/pages/SafetyPage'));
// Employee rebuild W7 (C10, C14): my safety, the roll call, and the admin
// screens behind announcements and safety.
const MySafetyPage = lazy(() => import('@/pages/MySafetyPage'));
const RollCallPage = lazy(() => import('@/pages/RollCallPage'));
const AdminAnnouncementsPage = lazy(() => import('@/pages/AdminAnnouncementsPage'));
const AdminSafetyPage = lazy(() => import('@/pages/AdminSafetyPage'));
const HybridPage = lazy(() => import('@/pages/HybridPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'));
const SuperAdminOrgsPage = lazy(() => import('@/pages/SuperAdminOrgsPage'));
const TenantPortalPage = lazy(() => import('@/pages/TenantPortalPage'));
const ContractorPortalPage = lazy(() => import('@/pages/ContractorPortalPage'));
const SupportInboxPage = lazy(() => import('@/pages/SupportInboxPage'));
const UnmatchedDataRequestsPage = lazy(() => import('@/pages/UnmatchedDataRequestsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const FMManagerMobilePage = lazy(() => import('@/pages/FMManagerMobilePage'));
const ContractorRegisterPage = lazy(() => import('@/pages/ContractorRegisterPage'));
const RoomPanelPage = lazy(() => import('@/pages/RoomPanelPage'));
const LobbySignagePage = lazy(() => import('@/pages/LobbySignagePage'));
const SpaceOptimiserPage = lazy(() => import('@/pages/SpaceOptimiserPage'));
const IntegrationsHubPage = lazy(() => import('@/pages/IntegrationsHubPage'));
const WorkflowsPage = lazy(() => import('@/pages/WorkflowsPage'));
const FormsPage = lazy(() => import('@/pages/FormsPage'));
const RolesPage = lazy(() => import('@/pages/RolesPage'));
const BadgeDesignerPage = lazy(() => import('@/pages/BadgeDesignerPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const NotificationTemplatesPage = lazy(() => import('@/pages/NotificationTemplatesPage'));
const EmailSequencesPage = lazy(() => import('@/pages/EmailSequencesPage'));
const SiriShortcutsPage = lazy(() => import('@/pages/SiriShortcutsPage'));

// New feature pages
const QRScannerPage = lazy(() => import('@/pages/QRScannerPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const DSARPage = lazy(() => import('@/pages/DSARPage'));
const RoadmapPage = lazy(() => import('@/pages/RoadmapPage'));
const DataResidencyPage = lazy(() => import('@/pages/DataResidencyPage'));

// Compliance & Reports feature pages
const ChecklistBuilderPage = lazy(() => import('@/pages/ChecklistBuilderPage'));
const IntegrationMarketplacePage = lazy(() => import('@/pages/IntegrationMarketplacePage'));
const MarketplacePage = lazy(() => import('@/pages/MarketplacePage'));
const VisitorCheckinPage = lazy(() => import('@/pages/VisitorCheckinPage'));
const PublicLinkPage = lazy(() => import('@/pages/PublicLinkPage'));

// ---------------------------------------------------------------------------
// Protected route wrapper
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** `/` — this person's home: My Day for the employee experience, the
 *  dashboard for everyone else (Employee rebuild C1). */
function HomeIndex() {
  const user = useAuthStore((s) => s.user);
  return <Navigate to={homePathFor(user)} replace />;
}

// ---------------------------------------------------------------------------
// Suspense wrapper
// ---------------------------------------------------------------------------

// Every route's element goes through here, and inside the protected routes that
// is inside AppLayout's <Outlet />. Wrapping the boundary here means a render
// error in one page shows that page's error state and leaves the shell — nav,
// sidebar, org switcher — usable, instead of the app-level boundary in main.tsx
// replacing the entire UI. A single unguarded lookup took down /spaces
// completely; this is what stops the next one from doing the same.
//
// Keyed on pathname so the boundary remounts on navigation. Without the key a
// crashed route stays crashed after the user navigates away, because React
// keeps the errored boundary state for the same element position.
//
// It is also where every route is GATED (Deep-test ADM-006): RouteGate looks
// the path up in config/routeAccess.ts — the map the sidebar reads too — and
// shows "You don't have access to this page" instead of mounting a page whose
// every request would be refused. Gated outside Suspense, so a page the user
// cannot open is never even downloaded.
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <RouteGate>
      <Suspense fallback={<LoadingSpinner fullPage label="Loading..." />}>
        <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
      </Suspense>
    </RouteGate>
  );
}

// ---------------------------------------------------------------------------
// App routes
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/register"
        element={
          <SuspenseWrapper>
            <RegisterPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <SuspenseWrapper>
            <ForgotPasswordPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/set-password"
        element={
          <SuspenseWrapper>
            <SetPasswordPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/reset-password"
        element={
          <SuspenseWrapper>
            <SetPasswordPage />
          </SuspenseWrapper>
        }
      />
      {/* 🔴 A PUBLIC ROUTE, OUTSIDE THE STAFF SHELL. A tenant portal token
          carries no permission set and a `sub` that is a tenant_users id, so
          /auth/me refuses it and every staff route rejects it by design.
          Putting this inside the authenticated layout would mean a sidebar
          that spends its life hiding itself. It holds its own session — see
          utils/portalSession — and the two never disturb each other. */}
      <Route
        path="/tenant-portal/*"
        element={
          <SuspenseWrapper>
            <TenantPortalPage />
          </SuspenseWrapper>
        }
      />
      {/* The contractor portal's door (Item_01) — public and outside the
          staff shell for the tenant portal's reason above, with its own
          session under its own key. */}
      <Route
        path="/contractor-portal/*"
        element={
          <SuspenseWrapper>
            <ContractorPortalPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/visitor-pass/:token"
        element={
          <SuspenseWrapper>
            <VisitorPassPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/visitors/kiosk"
        element={
          <SuspenseWrapper>
            <KioskPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/contractor/register"
        element={
          <SuspenseWrapper>
            <ContractorRegisterPage />
          </SuspenseWrapper>
        }
      />
      {/* 🔴 THE LINK THE SERVER SENDS. routers/contractor.py builds
          /contractor-register?invite=…, and only /contractor/register was a
          route — every invite link landed on the login page (found live,
          1 Oct). Both spellings open the page; links already sent keep working. */}
      <Route
        path="/contractor-register"
        element={
          <SuspenseWrapper>
            <ContractorRegisterPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/rooms/:id/panel"
        element={
          <SuspenseWrapper>
            <RoomPanelPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/signage"
        element={
          <SuspenseWrapper>
            <LobbySignagePage />
          </SuspenseWrapper>
        }
      />
      {/* Public: Pricing page (no auth required) */}
      <Route
        path="/pricing"
        element={
          <SuspenseWrapper>
            <PricingPage />
          </SuspenseWrapper>
        }
      />
      {/* Public: a data subject request. No sign-in — the people it is for
          (visitors, former staff) mostly have no account to sign in with. */}
      <Route
        path="/privacy/request"
        element={
          <SuspenseWrapper>
            <DSARPage />
          </SuspenseWrapper>
        }
      />
      {/* Public: Roadmap page */}
      <Route
        path="/roadmap"
        element={
          <SuspenseWrapper>
            <RoadmapPage />
          </SuspenseWrapper>
        }
      />
      {/* Public: a signed link. ONE route for every kind — the kind is a
          SIGNED claim inside the token, so putting it in the URL would be a
          second, unsigned answer to the same question. */}
      {/* Public: the "download my data" link from the email (Employee
          rebuild B28). The signed token is the authority, for 24 hours. */}
      <Route
        path="/download/my-data/:token"
        element={
          <SuspenseWrapper>
            <MyDataDownloadPage />
          </SuspenseWrapper>
        }
      />
      <Route
        path="/public/link/:token"
        element={
          <SuspenseWrapper>
            <PublicLinkPage />
          </SuspenseWrapper>
        }
      />
      {/* Public: Visitor QR self-check-in (Feature #34) */}
      <Route
        path="/visitor-checkin/:token"
        element={
          <SuspenseWrapper>
            <VisitorCheckinPage />
          </SuspenseWrapper>
        }
      />

      {/* Protected routes with app layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeIndex />} />
        <Route
          path="/dashboard"
          element={
            <SuspenseWrapper>
              <HomePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm"
          element={
            <SuspenseWrapper>
              <FmHomePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/tickets"
          element={
            <SuspenseWrapper>
              <FmTicketsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/spot-checks"
          element={
            <SuspenseWrapper>
              <FmSpotChecksPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/logbooks"
          element={
            <SuspenseWrapper>
              <FmLogbooksPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/ppm"
          element={
            <SuspenseWrapper>
              <FmPpmPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/assets"
          element={
            <SuspenseWrapper>
              <FmAssetsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/permits"
          element={
            <SuspenseWrapper>
              <FmPermitsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/drills"
          element={
            <SuspenseWrapper>
              <FmDrillsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/incidents"
          element={
            <SuspenseWrapper>
              <FmIncidentsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/designated-persons"
          element={
            <SuspenseWrapper>
              <FMDesignatedPersonsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/tools"
          element={
            <SuspenseWrapper>
              <FMToolsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/locks"
          element={
            <SuspenseWrapper>
              <FMLocksPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/technicians"
          element={
            <SuspenseWrapper>
              <FMTechniciansPage />
            </SuspenseWrapper>
          }
        />
        <Route path="/pm/no-building" element={<SuspenseWrapper><PmNoBuildingPage /></SuspenseWrapper>} />
        <Route path="/pm" element={<SuspenseWrapper><PmHomePage /></SuspenseWrapper>} />
        <Route path="/pm/calendar" element={<SuspenseWrapper><PmCalendarPage /></SuspenseWrapper>} />
        <Route path="/pm/rent-roll" element={<SuspenseWrapper><PmRentRollPage /></SuspenseWrapper>} />
        <Route path="/pm/tenancies/new" element={<SuspenseWrapper><PmTenancyFormPage /></SuspenseWrapper>} />
        <Route path="/pm/tenancies/:id" element={<SuspenseWrapper><PmTenancyPage /></SuspenseWrapper>} />
        <Route path="/pm/tenancies/:id/edit" element={<SuspenseWrapper><PmTenancyFormPage /></SuspenseWrapper>} />
        <Route path="/pm/tenants" element={<SuspenseWrapper><PmTenantsPage /></SuspenseWrapper>} />
        <Route path="/pm/tenants/:id" element={<SuspenseWrapper><PmTenant360Page /></SuspenseWrapper>} />
        <Route path="/pm/billing" element={<SuspenseWrapper><PmBillingPage /></SuspenseWrapper>} />
        <Route path="/pm/collections" element={<SuspenseWrapper><PmCollectionsPage /></SuspenseWrapper>} />
        <Route path="/pm/deposits" element={<SuspenseWrapper><PmDepositsPage /></SuspenseWrapper>} />
        <Route path="/pm/approvals" element={<SuspenseWrapper><PmApprovalsPage /></SuspenseWrapper>} />
        <Route path="/pm/profile" element={<SuspenseWrapper><PmProfilePage /></SuspenseWrapper>} />
        <Route
          path="/reception/no-building"
          element={
            <SuspenseWrapper>
              <NoBuildingPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/reception"
          element={
            <SuspenseWrapper>
              <AdminReceptionPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception"
          element={
            <SuspenseWrapper>
              <ReceptionHomePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/check-in"
          element={
            <SuspenseWrapper>
              <ReceptionCheckInPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/visitors"
          element={
            <SuspenseWrapper>
              <ReceptionVisitorsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/pre-register"
          element={
            <SuspenseWrapper>
              <ReceptionPreRegisterPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/approvals"
          element={
            <SuspenseWrapper>
              <ReceptionApprovalsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/watchlist"
          element={
            <SuspenseWrapper>
              <ReceptionWatchlistPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/alerts"
          element={
            <SuspenseWrapper>
              <ReceptionAlertsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/profile"
          element={
            <SuspenseWrapper>
              <ReceptionProfilePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/registers"
          element={
            <SuspenseWrapper>
              <ReceptionRegistersPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/mailroom"
          element={
            <SuspenseWrapper>
              <ReceptionMailroomPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/gate-passes"
          element={
            <SuspenseWrapper>
              <ReceptionGatePassesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/contractor-workers"
          element={
            <SuspenseWrapper>
              <ReceptionContractorWorkersPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/gate-passes/new"
          element={
            <SuspenseWrapper>
              <ReceptionGatePassNewPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/gate-pass-decision/:id"
          element={
            <SuspenseWrapper>
              <GatePassDecisionPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/keys-cards"
          element={
            <SuspenseWrapper>
              <ReceptionKeysCardsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/lost-found"
          element={
            <SuspenseWrapper>
              <ReceptionLostFoundPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/blank-register"
          element={
            <SuspenseWrapper>
              <BlankRegisterPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/visit-decision/:id"
          element={
            <SuspenseWrapper>
              <VisitDecisionPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/visitor-answer/:id"
          element={
            <SuspenseWrapper>
              <HostAnswerPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/rooms"
          element={
            <SuspenseWrapper>
              <ReceptionRoomsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/parking"
          element={
            <SuspenseWrapper>
              <ReceptionParkingPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/emergency"
          element={
            <SuspenseWrapper>
              <ReceptionEmergencyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/roll-call"
          element={
            <SuspenseWrapper>
              <ReceptionRollCallPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/drills"
          element={
            <SuspenseWrapper>
              <ReceptionDrillLogPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/concern"
          element={
            <SuspenseWrapper>
              <ReceptionConcernPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/concerns"
          element={
            <SuspenseWrapper>
              <ConcernInboxPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/daily-report"
          element={
            <SuspenseWrapper>
              <ReceptionDailyReportPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/handover"
          element={
            <SuspenseWrapper>
              <ReceptionHandoverPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/reception/visitor-log"
          element={
            <SuspenseWrapper>
              <ReceptionVisitorLogPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/no-site"
          element={
            <SuspenseWrapper>
              <NoSitePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/incident"
          element={
            <SuspenseWrapper>
              <TechIncidentPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/handover"
          element={
            <SuspenseWrapper>
              <TechHandoverPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/lead"
          element={
            <SuspenseWrapper>
              <TechLeadPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/my-work"
          element={
            <SuspenseWrapper>
              <TechMyWorkPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/permits"
          element={
            <SuspenseWrapper>
              <TechPermitsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/locks"
          element={
            <SuspenseWrapper>
              <TechLocksPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/emergency"
          element={
            <SuspenseWrapper>
              <TechEmergencyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/tools"
          element={
            <SuspenseWrapper>
              <TechToolsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/permits/:id"
          element={
            <SuspenseWrapper>
              <TechPermitPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech"
          element={
            <SuspenseWrapper>
              <TechHomePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/alerts"
          element={
            <SuspenseWrapper>
              <TechAlertsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/jobs"
          element={
            <SuspenseWrapper>
              <TechJobsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/profile"
          element={
            <SuspenseWrapper>
              <TechProfilePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/jobs/:kind/:id"
          element={
            <SuspenseWrapper>
              <TechJobPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/welcome"
          element={
            <SuspenseWrapper>
              <TechFirstRunPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/sync"
          element={
            <SuspenseWrapper>
              <TechSyncPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/scan"
          element={
            <SuspenseWrapper>
              <TechScanPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/assets/:id"
          element={
            <SuspenseWrapper>
              <TechAssetPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/report-fault"
          element={
            <SuspenseWrapper>
              <TechReportFaultPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/rounds"
          element={
            <SuspenseWrapper>
              <TechRoundsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tech/rounds/:templateId"
          element={
            <SuspenseWrapper>
              <TechRoundPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/safety-incidents/:id"
          element={
            <SuspenseWrapper>
              <SafetyIncidentPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/vendors"
          element={
            <SuspenseWrapper>
              <FmVendorsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/amc"
          element={
            <SuspenseWrapper>
              <FmAmcPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/invoices"
          element={
            <SuspenseWrapper>
              <FmInvoicesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/compliance"
          element={
            <SuspenseWrapper>
              <FmCompliancePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/reports"
          element={
            <SuspenseWrapper>
              <FmReportsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/kpis"
          element={
            <SuspenseWrapper>
              <FmKpiPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/settings/proof"
          element={
            <SuspenseWrapper>
              <FMProofRulesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm/settings"
          element={
            <SuspenseWrapper>
              <FmSettingsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-day"
          element={
            <SuspenseWrapper>
              <MyDayPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-bookings"
          element={
            <SuspenseWrapper>
              <MyBookingsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/book"
          element={
            <SuspenseWrapper>
              <BookPage />
            </SuspenseWrapper>
          }
        />
        {/* Employee rebuild C6 / C7 — staff keep /helpdesk unchanged. */}
        <Route
          path="/my-requests"
          element={
            <SuspenseWrapper>
              <MyRequestsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-requests/:id"
          element={
            <SuspenseWrapper>
              <MyRequestsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-equipment"
          element={
            <SuspenseWrapper>
              <MyEquipmentPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/office-days"
          element={
            <SuspenseWrapper>
              <OfficeDaysPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/whos-in"
          element={
            <SuspenseWrapper>
              <WhosInPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-visitors"
          element={
            <SuspenseWrapper>
              <MyVisitorsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/notifications"
          element={
            <SuspenseWrapper>
              <NotificationsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/spaces"
          element={
            <SuspenseWrapper>
              <SpacePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/helpdesk"
          element={
            <SuspenseWrapper>
              <HelpdeskPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/helpdesk/:id"
          element={
            <SuspenseWrapper>
              <TicketDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/maintenance"
          element={
            <SuspenseWrapper>
              <MaintenancePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/maintenance/work-orders/:id"
          element={
            <SuspenseWrapper>
              <WorkOrderDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/assets"
          element={
            <SuspenseWrapper>
              <AssetsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/assets/:id"
          element={
            <SuspenseWrapper>
              <AssetDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/visitors"
          element={
            <SuspenseWrapper>
              <VisitorPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/visitors/:id"
          element={
            <SuspenseWrapper>
              <VisitorDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/leases"
          element={
            <SuspenseWrapper>
              <LeasePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/leases/:id"
          element={
            <SuspenseWrapper>
              <LeaseDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/contractors"
          element={
            <SuspenseWrapper>
              <ContractorPage />
            </SuspenseWrapper>
          }
        />
        {/* One contractor's own page (Bug_59). The Credentials tab once
            navigated here when no such route existed and landed on the
            dashboard; it is real now. */}
        <Route
          path="/contractors/:id"
          element={
            <SuspenseWrapper>
              <ContractorProfilePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/iot/machines/:machineId"
          element={
            <SuspenseWrapper>
              <MachineDetailPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/iot"
          element={
            <SuspenseWrapper>
              <IoTPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tenants"
          element={
            <SuspenseWrapper>
              <TenantPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/tenant-ops"
          element={
            <SuspenseWrapper>
              <TenantOpsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/parking"
          element={
            <SuspenseWrapper>
              <ParkingPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/wellbeing"
          element={
            <SuspenseWrapper>
              <WellbeingPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/energy"
          element={
            <SuspenseWrapper>
              <EnergyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/compliance"
          element={
            <SuspenseWrapper>
              <CompliancePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/safety"
          element={
            <SuspenseWrapper>
              <SafetyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/my-safety"
          element={
            <SuspenseWrapper>
              <MySafetyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/safety/roll-call/:alertId"
          element={
            <SuspenseWrapper>
              <RollCallPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <SuspenseWrapper>
              <AdminAnnouncementsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/safety"
          element={
            <SuspenseWrapper>
              <AdminSafetyPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/hybrid"
          element={
            <SuspenseWrapper>
              <HybridPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/space/optimiser"
          element={
            <SuspenseWrapper>
              <SpaceOptimiserPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/analytics"
          element={
            <SuspenseWrapper>
              <AnalyticsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin"
          element={
            <SuspenseWrapper>
              <AdminPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/users"
          element={
            <SuspenseWrapper>
              <UserManagementPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/super-admin/organisations"
          element={
            <SuspenseWrapper>
              <SuperAdminOrgsPage />
            </SuspenseWrapper>
          }
        />
        {/* The only screen that reads across tenants. Its API refuses anybody
            who is not a super admin; the page redirects as well, so a wrong
            link lands somebody on their dashboard rather than on an error. */}
        <Route
          path="/super-admin/support"
          element={
            <SuspenseWrapper>
              <SupportInboxPage />
            </SuspenseWrapper>
          }
        />
        {/* Public data requests that reached no organisation. Super admin
            only, like the inbox: they belong to no tenant. */}
        <Route
          path="/super-admin/data-requests"
          element={
            <SuspenseWrapper>
              <UnmatchedDataRequestsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/integrations"
          element={
            <SuspenseWrapper>
              <IntegrationsHubPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/workflows"
          element={
            <SuspenseWrapper>
              <WorkflowsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/forms"
          element={
            <SuspenseWrapper>
              <FormsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <SuspenseWrapper>
              <RolesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/badge-designer"
          element={
            <SuspenseWrapper>
              <BadgeDesignerPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <SuspenseWrapper>
              <ReportsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/notification-templates"
          element={
            <SuspenseWrapper>
              <NotificationTemplatesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/admin/email-sequences"
          element={
            <SuspenseWrapper>
              <EmailSequencesPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/settings/siri-shortcuts"
          element={
            <SuspenseWrapper>
              <SiriShortcutsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/settings"
          element={
            <SuspenseWrapper>
              <SettingsPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/profile"
          element={
            <SuspenseWrapper>
              <ProfilePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/fm-mobile"
          element={
            <SuspenseWrapper>
              <FMManagerMobilePage />
            </SuspenseWrapper>
          }
        />
        {/* QR Scanner page (/scan) */}
        <Route
          path="/scan"
          element={
            <SuspenseWrapper>
              <QRScannerPage />
            </SuspenseWrapper>
          }
        />
        {/* Catering menu and orders (Employee rebuild C14 / B11) */}
        <Route
          path="/admin/catering"
          element={
            <SuspenseWrapper>
              <AdminCateringPage />
            </SuspenseWrapper>
          }
        />
        {/* Data Residency admin page */}
        <Route
          path="/admin/data-residency"
          element={
            <SuspenseWrapper>
              <DataResidencyPage />
            </SuspenseWrapper>
          }
        />
        {/* Checklist Builder */}
        <Route
          path="/maintenance/checklists"
          element={
            <SuspenseWrapper>
              <ChecklistBuilderPage />
            </SuspenseWrapper>
          }
        />
        {/* Integration Marketplace */}
        <Route
          path="/marketplace/integrations"
          element={
            <SuspenseWrapper>
              <IntegrationMarketplacePage />
            </SuspenseWrapper>
          }
        />
        {/* Parking Revenue Marketplace */}
        <Route
          path="/marketplace/parking"
          element={
            <SuspenseWrapper>
              <MarketplacePage />
            </SuspenseWrapper>
          }
        />
        {/* Catch-all — DSH-043: "Page not found", inside the app, keeping the
            address. It was a silent <Navigate> to /dashboard. Inside the
            protected layout, so a signed-out visitor is still sent to sign in. */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
